// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type PartCatalog, useEvolveDelete, useEvolvePatch } from '@evolvemep/api-ts';
import { TextInput, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { GridMenu } from 'components/Mantine/GridMenu';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { isNotNil } from 'helpers/isNotNil';

type Props = {
  partCatalog: PartCatalog | null;
  onCatalogUpdated: (updatedPartCatalog: PartCatalog) => void;
  onCatalogDeleted: () => void;
};

export const EditCatalogPopover = ({ partCatalog, onCatalogUpdated, onCatalogDeleted }: Props) => {
  const [catalogName, setCatalogName] = useState(partCatalog?.name ?? '');
  const { apiCall: updateCatalogApiCall, loading: updating } = useEvolvePatch(`shop/partCatalog/:partCatalogId`);

  const { apiCall: deleteCatalogApiCall, loading: deleting } = useEvolveDelete(`shop/partCatalog/:partCatalogId`);

  return (
    <GridMenu
      options={['Rename', 'Delete']}
      disabled={!partCatalog?.editable}
      variant="light"
      size="input-sm"
      icon="Settings"
      iconSize="md"
      tooltip="Edit catalog"
      onMenuItemSelected={(menuItemSelected) => {
        setCatalogName(menuItemSelected === 'Rename' ? (partCatalog?.name ?? '') : '');
        return false;
      }}
    >
      {(menuItemSelected, resetMenu) => (
        <>
          <WrappedModal
            title="Rename catalog"
            opened={menuItemSelected === 'Rename'}
            onClose={resetMenu}
            loading={updating}
            onConfirm={async () => {
              if (isNotNil(partCatalog)) {
                await updateCatalogApiCall(
                  { name: catalogName },
                  {
                    url: `shop/partCatalog/${partCatalog.partCatalogId}`,
                  },
                )
                  .then(onCatalogUpdated)
                  .then(resetMenu);
              }
            }}
            confirmationText="Rename"
            disabled={catalogName.trim() === partCatalog?.name.trim() || !catalogName.trim()}
          >
            <TextInput
              label="Catalog Name"
              value={catalogName}
              onChange={(e) => setCatalogName(e.target.value)}
              disabled={updating}
              autoFocus
              data-autofocus
              required
              maxLength={64}
            />
          </WrappedModal>
          <WrappedModal
            title="Delete catalog"
            confirmationText="Delete"
            opened={menuItemSelected === 'Delete'}
            onClose={resetMenu}
            loading={deleting}
            disabled={catalogName.trim() !== partCatalog?.name.trim()}
            onConfirm={async () => {
              if (isNotNil(partCatalog)) {
                await deleteCatalogApiCall({
                  url: `shop/partCatalog/${partCatalog.partCatalogId}`,
                }).then(() => {
                  onCatalogDeleted();
                  resetMenu();
                  notifications.show({
                    title: 'Successfully deleted',
                    message: `Deleted catalog ${partCatalog.name}.`,
                    color: 'green',
                  });
                });
              }
            }}
          >
            <Text>
              Deleting the catalog <b>{partCatalog?.name}</b> will permanently delete all items within the catalog.
            </Text>
            <Text>To confirm, type the name of the catalog below.</Text>
            <TextInput
              placeholder="Catalog name"
              value={catalogName}
              onChange={(e) => setCatalogName(e.target.value)}
              disabled={deleting}
              required
              autoFocus
              data-autofocus
            />
          </WrappedModal>
        </>
      )}
    </GridMenu>
  );
};
